import { Text } from '@/components/ui/text';
import { UserAvatar } from '@/components/ui/user-avatar';
import { User } from '@/types';
import React from 'react';
import { View } from 'react-native';

interface ProfileHeaderProps {
  user: User;
  friendshipBadge?: React.ReactNode;
}

const AVATAR_SIZE = 96;

/**
 * The profile identity block: avatar, name, @handle, bio, and joined date.
 *
 * Hierarchy is deliberately two-tier. The name is the screen's hero (Display
 * size) and the bio is content in the `foreground` tier — it carries the
 * person's voice, so it must read brighter than metadata. The @handle and the
 * joined date sit in the quiet `muted-foreground` tier (still AA-safe on the
 * near-black background; `dim` would fail contrast). The share/QR control lives
 * in the screen's header actions, not here.
 */
export function ProfileHeader({ user, friendshipBadge }: ProfileHeaderProps) {
  const userName = user?.name || 'Unknown User';
  const rawHandle = user?.username || user?.displayUsername || '';
  const normalizedHandle = rawHandle.replace(/^@+/, '');
  const userUsername = normalizedHandle ? `@${normalizedHandle}` : '@unknown';

  return (
    <View className="items-center mb-8">
      <UserAvatar
        uri={user?.image || user?.avatar}
        name={user?.name}
        size={AVATAR_SIZE}
        accessibilityLabel={`${userName}'s profile photo`}
        className="border-border-visible bg-surface mb-5"
        style={{ borderWidth: 1.5 }}
      />

      <Text
        className="text-4xl font-bold tracking-tight text-center text-foreground"
        accessibilityRole="header"
      >
        {userName}
      </Text>

      {friendshipBadge && <View className="mt-2">{friendshipBadge}</View>}

      <Text className="text-base mt-1.5 text-muted-foreground">{userUsername}</Text>

      {user?.bio && (
        <Text
          className="text-base leading-6 mt-3 text-center text-foreground"
          style={{ maxWidth: 320 }}
        >
          {user.bio}
        </Text>
      )}

      {user?.createdAt && (
        <Text className="text-sm mt-2 text-muted-foreground">
          Joined{' '}
          {new Date(user.createdAt).toLocaleDateString('en-US', {
            month: 'long',
            year: 'numeric',
          })}
        </Text>
      )}
    </View>
  );
}
